import { useState } from 'react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Button,
  ButtonGroup,
  Stack,
  InlineStack,
  Text,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';

/** The modal's own words, for a surface that asks the question in its own terms. */
export type ConfirmLeaveCopy = {
  /** The title. `Leave without saving?` by default. */
  title?: string;
  /** The button that keeps the user where they are. `Cancel` by default. */
  cancelLabel?: string;
  /** The button that saves and then goes on. `Save and continue` by default. */
  saveLabel?: string;
};

export type ConfirmLeaveModalData = ConfirmLeaveCopy & {
  /**
   * Custom message to show instead of the default unsaved-changes copy
   */
  message?: string;

  /**
   * Callback to save changes before navigating away.
   * If provided, a "Save and continue" option will be shown.
   */
  onSave?: () => Promise<void> | void;

  /**
   * Renders "Save and continue" disabled instead of hiding it — for a form that
   * has a save action but can't run it yet (failing validation, say). Ember's
   * `cio-save-continue-modal` kept the button visible and disabled in this
   * state; hiding the primary action instead removes the user's anchor for what
   * they are meant to do once they have fixed the problem. Pair it with
   * `message` so the prompt explains why.
   */
  isSaveDisabled?: boolean;

  /**
   * Why "Save and continue" is disabled, shown in its tooltip. Only read while
   * `isSaveDisabled` is true.
   */
  saveDisabledReason?: string | null;

  /**
   * Callback to handle errors from the save operation
   */
  onError?: (error: unknown) => void;

  /**
   * Callback called when user chooses to discard changes
   */
  onDiscard?: () => void;
};

const DEFAULT_TITLE = 'Leave without saving?';
const DEFAULT_CANCEL_LABEL = 'Cancel';
const DEFAULT_SAVE_LABEL = 'Save and continue';

/**
 * Managed confirm-leave modal shown by `NavigationGuardProvider` when a
 * navigation is attempted with unsaved changes.
 *
 * Outcomes (observed by the provider via `modals.waitForClose`):
 * - `onConfirm()` — proceed with the blocked navigation ("Discard changes",
 *   or "Save and continue" after a successful save)
 * - `onClose()` — cancel the navigation ("Cancel", Esc, overlay click, or a
 *   failed save)
 */
export function ConfirmLeaveModal({
  modalState,
  data,
  onClose,
  onConfirm,
}: PlumaManagedModalProps<ConfirmLeaveModalData>) {
  const [isSaving, setIsSaving] = useState(false);
  const {
    title = DEFAULT_TITLE,
    cancelLabel = DEFAULT_CANCEL_LABEL,
    saveLabel = DEFAULT_SAVE_LABEL,
    message,
    onSave,
    isSaveDisabled = false,
    saveDisabledReason = null,
    onError,
    onDiscard,
  } = data;

  // Normalizes args: the Modal calls `onClose(event, reason)`, but the
  // manager's close callback treats its first argument as close data.
  const handleCancel = () => {
    onClose();
  };

  const handleDiscardAndContinue = () => {
    onDiscard?.();
    onConfirm();
  };

  const handleSaveAndContinue = async () => {
    // `isSaveDisabled` must gate the handler too, not just the button: saving
    // is impossible in that state, and letting it through would call
    // `onConfirm()` on a no-op save and navigate away with the changes lost.
    // A save already in flight is refused here too, so a second click cannot
    // run `onSave` twice whatever the button does with it.
    if (onSave == null || isSaveDisabled || isSaving) {
      return;
    }

    try {
      setIsSaving(true);
      await onSave();
      onConfirm();
    } catch (error) {
      // Close the modal on save failure so the underlying page —
      // which typically owns the error UI (inline banner, form-field
      // errors) — becomes visible again. Without this, the modal
      // sits over the page hiding whatever feedback `onSave` already
      // rendered, and the user is left with only a stopped spinner.
      onClose();
      onError?.(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      state={modalState}
      onClose={handleCancel}
      title={title}
      // Veto dismissals (Esc, overlay click, close button) while a save is
      // in flight — closing mid-save would let the pending `onSave` promise
      // resolve against a dismissed modal and leave the blocked navigation
      // in an ambiguous state. Explicit props win over the manager's `state`.
      isDismissable={!isSaving}
      // `isDismissable={false}` hides the close button by default; keep it
      // visible (rendered disabled) so the header doesn't jump mid-save.
      shouldShowCloseButton
    >
      <ModalBody>
        {message != null && !isEmptyString(message) ? (
          <Paragraph>{message}</Paragraph>
        ) : (
          <Stack gap="200">
            <Paragraph>You have unsaved changes.</Paragraph>
            <Paragraph>Save your changes or discard them to continue.</Paragraph>
          </Stack>
        )}
      </ModalBody>
      <ModalFooter as={InlineStack} alignBlock="center" alignInline="space-between" gap="200">
        <Button variant="secondary" size="sm" onClick={handleCancel} disabled={isSaving}>
          {cancelLabel}
        </Button>
        <ButtonGroup groupVariant="spaced">
          <Button variant="secondary" size="sm" isCritical onClick={handleDiscardAndContinue} disabled={isSaving}>
            Discard changes
          </Button>
          {onSave != null ? (
            <Tooltip isCustom isDisabled={!isSaveDisabled || saveDisabledReason == null}>
              {(context) => (
                <>
                  <Button
                    ref={context.reference.setReference}
                    {...context.reference.getReferenceProps()}
                    variant="primary"
                    size="sm"
                    onClick={handleSaveAndContinue}
                    isLoading={isSaving}
                    // `isDisabled` rather than the native attribute, so a
                    // disabled button still takes the hover and focus that
                    // show its tooltip. `handleSaveAndContinue` refuses the
                    // click itself.
                    isDisabled={isSaving || isSaveDisabled}
                  >
                    {saveLabel}
                  </Button>
                  {isSaveDisabled && saveDisabledReason != null ? (
                    <TooltipContent>
                      <Text color="inherit">{saveDisabledReason}</Text>
                    </TooltipContent>
                  ) : null}
                </>
              )}
            </Tooltip>
          ) : null}
        </ButtonGroup>
      </ModalFooter>
    </Modal>
  );
}
